import type { Schema } from '@cli-types/core-engine/schema.js'
import type { ActionsGeneratorResult } from '@cli-types/next-adapter/generators.js'
import { createGeneratedFile } from '@cli-utils/files.js'
import { toKebabCase } from '@cli-utils/text.js'
import { resolveSchemaActionDir } from '@next-adapter/config/paths/resolve-schema-action-dir.js'
import { genBarrelContent } from '@next-adapter/generators/actions/entity-file-contents/gen-barrel-content.js'
import { genBulkSortOrderContent } from '@next-adapter/generators/actions/entity-file-contents/gen-bulk-sort-order-content.js'
import { genCreateContent } from '@next-adapter/generators/actions/entity-file-contents/gen-create-content.js'
import { genCreateSelectOptionContent } from '@next-adapter/generators/actions/entity-file-contents/gen-create-select-option-content.js'
import { genDeleteBulkContent } from '@next-adapter/generators/actions/entity-file-contents/gen-delete-bulk-content.js'
import { genDeleteContent } from '@next-adapter/generators/actions/entity-file-contents/gen-delete-content.js'
import { genDistinctFiles } from '@next-adapter/generators/actions/entity-file-contents/gen-distinct-files.js'
import { genGetByIdContent } from '@next-adapter/generators/actions/entity-file-contents/gen-get-by-id-content.js'
import { genGetBySlugContent } from '@next-adapter/generators/actions/entity-file-contents/gen-get-by-slug-content.js'
import { genGetPluralContent } from '@next-adapter/generators/actions/entity-file-contents/gen-get-plural-content.js'
import { genGetSelectOptionsContent } from '@next-adapter/generators/actions/entity-file-contents/gen-get-select-options-content.js'
import { genGetVersionsContent } from '@next-adapter/generators/actions/entity-file-contents/gen-get-versions-content.js'
import { genHelpersContent } from '@next-adapter/generators/actions/entity-file-contents/gen-helpers-content.js'
import { genM2MFiles } from '@next-adapter/generators/actions/entity-file-contents/gen-m2m-files.js'
import { genRestoreContent } from '@next-adapter/generators/actions/entity-file-contents/gen-restore-content.js'
import { genTypesContent } from '@next-adapter/generators/actions/entity-file-contents/gen-types-content.js'
import { genUpdateContent } from '@next-adapter/generators/actions/entity-file-contents/gen-update-content.js'
import { genMarkdownRendererContent } from '@next-adapter/generators/actions/markdown-renderer.js'
import { buildEntityContext } from './build-entity-context.js'

export function generateActions(
  schema: Schema,
  actionsDir: string,
  options: {
    force?: boolean
    schemasDir?: string
    nextMajorVersion?: number
    outputNamespace?: string
  } = {}
): ActionsGeneratorResult {
  const ctx = buildEntityContext(schema, options.nextMajorVersion)
  const schemaActionDir = resolveSchemaActionDir(actionsDir, schema.name, options.outputNamespace)

  const kebabSingular = toKebabCase(ctx.singular)
  const kebabPlural = toKebabCase(ctx.plural)
  const files: Array<{ name: string; content: string }> = []

  files.push({ name: 'types.ts', content: genTypesContent(ctx) })

  const helpersContent = genHelpersContent(ctx)
  if (helpersContent) {
    files.push({ name: 'helpers.ts', content: helpersContent })
  }
  if (ctx.hasHtmlOutput) {
    files.push({ name: 'markdown.ts', content: genMarkdownRendererContent() })
  }

  files.push({ name: `get-${kebabPlural}.ts`, content: genGetPluralContent(ctx) })
  files.push({ name: `get-${kebabSingular}-by-id.ts`, content: genGetByIdContent(ctx) })
  if (ctx.hasCreatableSelectFields) {
    files.push({
      name: `get-${kebabSingular}-select-options.ts`,
      content: genGetSelectOptionsContent(ctx)
    })
    files.push({
      name: `create-${kebabSingular}-select-option.ts`,
      content: genCreateSelectOptionContent(ctx)
    })
  }

  const bySlugContent = genGetBySlugContent(ctx)
  if (bySlugContent) {
    files.push({ name: `get-${kebabSingular}-by-slug.ts`, content: bySlugContent })
  }

  files.push({ name: `create-${kebabSingular}.ts`, content: genCreateContent(ctx) })
  files.push({ name: `update-${kebabSingular}.ts`, content: genUpdateContent(ctx) })
  files.push({
    name: `get-versions-by-${kebabSingular}-id.ts`,
    content: genGetVersionsContent(ctx)
  })
  files.push({ name: `restore-${kebabSingular}-version.ts`, content: genRestoreContent(ctx) })
  files.push({ name: `delete-${kebabSingular}.ts`, content: genDeleteContent(ctx) })
  files.push({ name: `delete-bulk-${kebabPlural}.ts`, content: genDeleteBulkContent(ctx) })
  files.push({
    name: `bulk-update-${kebabPlural}-sort-order.ts`,
    content: genBulkSortOrderContent(ctx)
  })

  files.push(...genM2MFiles(ctx))

  files.push(...genDistinctFiles(ctx))

  files.push({ name: 'index.ts', content: genBarrelContent(files, ctx) })

  return {
    files: files.map((file) => createGeneratedFile(`${schemaActionDir}/${file.name}`, file.content))
  }
}
